import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgress, Tooltip } from "@material-ui/core";
import { parseISO, format } from "date-fns";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  stickerWrapper: {
    position: "relative",
    display: "inline-block",
    margin: "2px 0",
    borderRadius: 8,
    userSelect: "none"
  },
  stickerContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    maxWidth: 160,
    maxHeight: 160,
    minWidth: 100,
    minHeight: 100,
    backgroundColor: "transparent",
    borderRadius: 8,
    cursor: "pointer",
    overflow: "visible",
    transition: "transform 0.15s ease-in-out",
    "&:hover": {
      transform: "scale(1.02)"
    }
  },
  stickerImage: {
    maxWidth: 160,
    maxHeight: 160,
    width: "auto",
    height: "auto",
    objectFit: "contain",
    backgroundColor: "transparent",
    filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
    display: "block"
  },
  stickerTimestamp: {
    position: "absolute",
    bottom: 4,
    right: 6,
    fontSize: 11,
    padding: "2px 6px",
    borderRadius: 10,
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    color: "#ffffff !important",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    backdropFilter: "blur(2px)",
    lineHeight: 1.2,
    zIndex: 2,
    pointerEvents: "none"
  },
  stickerLoading: {
    width: 140,
    height: 140,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.mode === "light" ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.05)",
    borderRadius: 12
  },
  stickerError: {
    width: 140,
    height: 140,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.mode === "light" ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.06)",
    borderRadius: 12,
    border: `1px dashed ${theme.mode === "light" ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.2)"}`,
    color: theme.mode === "light" ? "#666" : "#aaa",
    fontSize: 12,
    cursor: "pointer",
    padding: 8,
    textAlign: "center"
  }
}));

const StickerItem = ({
  message,
  renderMessageAck,
  openMediaViewer
}) => {
  const classes = useStyles();
  const [imgSrc, setImgSrc] = useState(message.mediaUrl);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const retryCount = useRef(0);
  const blobUrlRef = useRef(null);

  // Limpa o object URL quando o componente desmonta
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        window.URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setImgSrc(message.mediaUrl);
    setLoading(true);
    setHasError(false);
    retryCount.current = 0;
  }, [message.mediaUrl, message.id]);

  const handleError = async () => {
    // Tenta fallback via API caso a URL estática direta falhe
    if (retryCount.current === 0) {
      retryCount.current += 1;
      try {
        // Tenta rota autenticada de download da mensagem
        const response = await api.get(`/messages/${message.id}/download`, {
          responseType: "blob"
        });
        const blobUrl = window.URL.createObjectURL(
          new Blob([response.data], { type: response.headers["content-type"] || "image/webp" })
        );
        blobUrlRef.current = blobUrl;
        setImgSrc(blobUrl);
        setHasError(false);
        return;
      } catch (err1) {
        // Se a rota download falhar, tenta rota direta com api
        if (message.mediaUrl) {
          try {
            const resp = await api.get(message.mediaUrl, { responseType: "blob" });
            const blobUrl = window.URL.createObjectURL(
              new Blob([resp.data], { type: resp.headers["content-type"] || "image/webp" })
            );
            blobUrlRef.current = blobUrl;
            setImgSrc(blobUrl);
            setHasError(false);
            return;
          } catch (err2) {
            console.error("Falha ao recuperar figurinha:", err2);
          }
        }
      }
    }
    setLoading(false);
    setHasError(true);
  };

  const handleManualRetry = (e) => {
    e.stopPropagation();
    retryCount.current = 0;
    setHasError(false);
    setLoading(true);
    setImgSrc(message.mediaUrl);
  };

  const formattedTime = message.createdAt
    ? format(parseISO(message.createdAt), "HH:mm")
    : "";

  return (
    <div className={classes.stickerWrapper}>
      {hasError ? (
        <Tooltip title="Clique para tentar recarregar a figurinha">
          <div className={classes.stickerError} onClick={handleManualRetry}>
            <span style={{ fontSize: 28, marginBottom: 4 }}>👾</span>
            <span>Figurinha</span>
            <span style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>
              Toque para recarregar
            </span>
            <span className={classes.stickerTimestamp}>
              {message.isEdited ? "Editada " : ""}
              {formattedTime}
              {message.fromMe && renderMessageAck && renderMessageAck(message)}
            </span>
          </div>
        </Tooltip>
      ) : (
        <div
          className={classes.stickerContainer}
          onClick={() => openMediaViewer && openMediaViewer(message)}
          title="Clique para ampliar a figurinha"
        >
          {loading && (
            <div className={classes.stickerLoading}>
              <CircularProgress size={24} style={{ opacity: 0.6 }} />
            </div>
          )}
          <img
            src={imgSrc}
            alt="Figurinha"
            className={classes.stickerImage}
            style={{ display: loading ? "none" : "block" }}
            loading="lazy"
            decoding="async"
            onLoad={() => setLoading(false)}
            onError={handleError}
          />
          <span className={classes.stickerTimestamp}>
            {message.isEdited ? "Editada " : ""}
            {formattedTime}
            {message.fromMe && renderMessageAck && renderMessageAck(message)}
          </span>
        </div>
      )}
    </div>
  );
};

export default StickerItem;

